import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Send, MessageSquare, ArrowLeft } from 'lucide-react';
import { ChannelIcon } from '@/components/modules/ChannelIcon';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import { formatRelative } from '@/lib/format';
import { useMessagingStore } from '@/stores/messagingStore';

const channelTabs = [
  { id: 'all', label: 'All' },
  { id: 'sms', label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'live_chat', label: 'Live Chat' },
  { id: 'messenger', label: 'Messenger' },
  { id: 'instagram', label: 'Instagram' },
];

const statusVariant: Record<string, 'success' | 'warning' | 'default'> = {
  active: 'success',
  waiting: 'warning',
  closed: 'default',
};

export function MessagingHub() {
  const {
    conversations,
    messages,
    selectedId,
    isLoading,
    error,
    fetchConversations,
    selectConversation,
    send,
    clearError,
  } = useMessagingStore();

  const [channelFilter, setChannelFilter] = useState('all');
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const filtered = conversations.filter(
    (c) => channelFilter === 'all' || c.channel === channelFilter,
  );

  const activeConvo = conversations.find((c) => c.id === selectedId);

  const handleSend = async () => {
    if (!messageInput.trim() || !selectedId || !activeConvo) return;
    setSending(true);
    try {
      await send({
        conversationId: selectedId,
        channel: activeConvo.channel,
        content: messageInput.trim(),
      });
      setMessageInput('');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getConvoLabel = (convo: typeof conversations[number]) => {
    const channelName = convo.channel.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const msgSuffix = `(${convo.messageCount} msg${convo.messageCount !== 1 ? 's' : ''})`;
    return `${channelName} ${msgSuffix}`;
  };

  if (isLoading && conversations.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div>
          <Skeleton className="h-6 w-36 mb-2" />
          <Skeleton className="h-4 w-72" />
        </div>
        {/* Channel tabs skeleton */}
        <div className="flex items-center gap-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-20 rounded-lg" />
          ))}
        </div>
        {/* Main layout: conversation list + chat area */}
        <div className="flex gap-0 h-[calc(100vh-260px)] bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
          {/* Conversation list skeleton */}
          <div className="w-80 border-r border-surface-700 p-2 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-3">
                <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
          {/* Chat area skeleton */}
          <div className="flex-1 flex flex-col">
            <div className="px-5 py-3 border-b border-surface-700">
              <Skeleton className="h-5 w-48" />
            </div>
            <div className="flex-1 p-5 space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                  <Skeleton className="h-12 w-2/5 rounded-xl" />
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-surface-700">
              <Skeleton className="h-10 w-full rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div>
        <h2 className="text-xl font-bold text-surface-100">Messaging Hub</h2>
        <p className="text-sm text-surface-400 mt-0.5">Unified conversations across SMS, WhatsApp, and Live Chat</p>
      </div>

      {error && (
        <div className="flex items-center justify-between px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="text-xs text-red-400 hover:text-red-300 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Channel tabs */}
      <Tabs tabs={channelTabs} active={channelFilter} onChange={setChannelFilter} />

      {/* Main layout */}
      <div className="flex gap-0 h-[calc(100vh-260px)] bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
        {/* Conversation list — hidden on mobile when a conversation is selected */}
        <div className={clsx(
          'w-full md:w-80 border-r border-surface-700 overflow-y-auto flex-shrink-0',
          selectedId ? 'hidden md:block' : 'block',
        )}>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <MessageSquare className="h-8 w-8 text-surface-600 mb-2" />
              <p className="text-sm text-surface-400">No conversations</p>
            </div>
          ) : (
            filtered.map((convo) => (
              <button
                key={convo.id}
                onClick={() => selectConversation(convo.id)}
                className={clsx(
                  'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-surface-700/50 cursor-pointer',
                  selectedId === convo.id ? 'bg-surface-700/50' : 'hover:bg-surface-700/30',
                )}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <ChannelIcon channel={convo.channel} size="md" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-surface-200 truncate">
                      {getConvoLabel(convo)}
                    </span>
                    <span className="text-[10px] text-surface-500 flex-shrink-0">
                      {convo.lastMessageAt ? formatRelative(convo.lastMessageAt) : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-surface-500 capitalize">{convo.channel.replace('_', ' ')}</span>
                    <Badge variant={statusVariant[convo.status] || 'default'}>{convo.status}</Badge>
                  </div>
                  <p className="text-xs text-surface-400 mt-0.5">
                    {convo.messageCount} message{convo.messageCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Chat thread — hidden on mobile when no conversation is selected */}
        <div className={clsx(
          'flex-1 flex flex-col min-w-0',
          selectedId ? 'block' : 'hidden md:flex',
        )}>
          {/* Chat header */}
          <div className="px-3 md:px-5 py-3 border-b border-surface-700 flex items-center gap-3">
            {/* Back button — mobile only */}
            {selectedId && (
              <button
                onClick={() => selectConversation(null)}
                className="md:hidden p-1.5 -ml-1 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            {activeConvo ? (
              <>
                <ChannelIcon channel={activeConvo.channel} size="md" />
                <div>
                  <p className="text-sm font-medium text-surface-200">{getConvoLabel(activeConvo)}</p>
                  <p className="text-xs text-surface-500 capitalize">{activeConvo.channel.replace('_', ' ')}</p>
                </div>
              </>
            ) : (
              <p className="text-sm text-surface-400">Select a conversation</p>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 md:p-5 space-y-3">
            {!selectedId ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageSquare className="h-10 w-10 text-surface-600 mb-3" />
                <p className="text-sm text-surface-400">Select a conversation to view messages</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageSquare className="h-10 w-10 text-surface-600 mb-3" />
                <p className="text-sm text-surface-400">No messages yet</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={clsx('flex', msg.direction === 'outbound' ? 'justify-end' : 'justify-start')}
                >
                  <div
                    className={clsx(
                      'max-w-[85%] md:max-w-[70%] px-4 py-2.5 rounded-xl',
                      msg.direction === 'outbound'
                        ? 'bg-nexus-600 text-white rounded-br-md'
                        : 'bg-surface-700 text-surface-200 rounded-bl-md',
                    )}
                  >
                    <p className="text-sm">{msg.content}</p>
                    <p
                      className={clsx(
                        'text-[10px] mt-1',
                        msg.direction === 'outbound' ? 'text-nexus-200' : 'text-surface-500',
                      )}
                    >
                      {msg.sender} · {formatRelative(msg.createdAt)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Input */}
          <div className="px-3 md:px-5 py-3 border-t border-surface-700">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Type a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!selectedId || sending}
                className="flex-1 px-4 py-2.5 bg-surface-900 border border-surface-700 rounded-xl text-sm text-surface-200 placeholder-surface-500 focus:outline-none focus:ring-1 focus:ring-nexus-500 disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!messageInput.trim() || !selectedId || sending}
                className={clsx(
                  'p-2.5 bg-nexus-600 text-white rounded-xl hover:bg-nexus-500 transition-colors disabled:opacity-50 disabled:pointer-events-none',
                  sending && 'animate-pulse',
                )}
              >
                {sending ? (
                  <span className="block h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
